import axios from 'axios';

const BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const client = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' }
});

// -- Token storage helpers --
export const setStoredToken = (token) => {
  if (token) localStorage.setItem('iremind_token', token);
  else localStorage.removeItem('iremind_token');
};
export const getStoredToken = () => localStorage.getItem('iremind_token');

// -- Request interceptor: always attach Bearer token if available --
client.interceptors.request.use(config => {
  const token = getStoredToken();
  if (token) config.headers['Authorization'] = `Bearer ${token}`;
  return config;
});

// -- Response interceptor: refresh on 401 --
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error) => {
  failedQueue.forEach(prom => error ? prom.reject(error) : prom.resolve());
  failedQueue = [];
};

client.interceptors.response.use(
  res => res,
  async err => {
    const original = err.config;
    if (err.response?.status === 401 && !original._retry && !original.url?.includes('/auth/')) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve: () => resolve(client(original)), reject });
        });
      }
      original._retry = true;
      isRefreshing = true;
      try {
        const res = await client.post('/api/auth/refresh');
        if (res.data?.token) setStoredToken(res.data.token);
        processQueue(null);
        return client(original);
      } catch (refreshErr) {
        setStoredToken(null);
        processQueue(refreshErr);
        return Promise.reject(refreshErr);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(err);
  }
);

export const authAPI = {
  register: (data) => client.post('/api/auth/register', data).then(r => r.data),
  login: (data) => client.post('/api/auth/login', data).then(r => r.data),
  logout: () => client.post('/api/auth/logout').then(r => r.data),
  me: () => client.get('/api/auth/me').then(r => r.data),
  forgotPassword: (email, lang = 'en') => client.post('/api/auth/forgot-password', { email, lang }).then(r => r.data),
  resetPassword: (email, code, new_password) => client.post('/api/auth/reset-password', { email, code, new_password }).then(r => r.data),
  googleSession:  (session_id) => client.post('/api/auth/google-session', { session_id }).then(r => r.data),
  googleExchange: (token)      => client.post('/api/auth/google/exchange',  { token      }).then(r => r.data),
};

export const remindersAPI = {
  getAll: () => client.get('/api/reminders').then(r => r.data),
  get: (id) => client.get(`/api/reminders/${id}`).then(r => r.data),
  create: (data) => client.post('/api/reminders', data).then(r => r.data),
  update: (id, data) => client.put(`/api/reminders/${id}`, data).then(r => r.data),
  delete: (id) => client.delete(`/api/reminders/${id}`).then(r => r.data),
};

export const groupsAPI = {
  getAll: () => client.get('/api/groups').then(r => r.data),
  get: (id) => client.get(`/api/groups/${id}`).then(r => r.data),
  create: (data) => client.post('/api/groups', data).then(r => r.data),
  rename: (id, name) => client.put(`/api/groups/${id}`, { name }).then(r => r.data),
  removeMember: (groupId, memberId) => client.delete(`/api/groups/${groupId}/members/${memberId}`).then(r => r.data),
  leave: (id) => client.post(`/api/groups/${id}/leave`).then(r => r.data),
  join: (id) => client.post(`/api/groups/${id}/join`).then(r => r.data),
  delete: (id) => client.delete(`/api/groups/${id}`).then(r => r.data),
  previewByToken: (token) => client.get(`/api/groups/invite-preview/${token}`).then(r => r.data),
  joinByToken: (token) => client.post('/api/groups/join-by-token', { token }).then(r => r.data),
  previewByCode: (code) => client.get(`/api/groups/code-preview/${code}`).then(r => r.data),
  joinByCode: (code) => client.post('/api/groups/join-by-code', { code }).then(r => r.data),
};

export const settingsAPI = {
  get: () => client.get('/api/settings').then(r => r.data),
  update: (data) => client.put('/api/settings', data).then(r => r.data),
};

export const accountAPI = {
  delete: () => client.delete('/api/account').then(r => r.data),
};

export const pushAPI = {
  getVapidKey: () => client.get('/api/push/vapid-key').then(r => r.data),
  subscribe: (subscription) => client.post('/api/push/subscribe', { subscription }).then(r => r.data),
  unsubscribe: (subscription) => client.post('/api/push/unsubscribe', { subscription }).then(r => r.data),
};

export const limitsAPI = {
  get: () => client.get('/api/user/limits').then(r => r.data),
};

export default client;
